import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { PrimaryLink, SecondaryLink } from 'jl-design-system/elements/link/Link';
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import Modal from 'jl-design-system/components/modal/Modal';
import Message from 'jl-design-system/components/message/Message';
// lodash
import get from 'lodash/get';
//
import { connect } from 'react-redux';
import {
  showLimitedStockModal,
  rebatchOrderAfterBasketEdit,
  batchUpdateItemQuantity,
} from '../../redux/actions/edit-basket/editBasketActions';
import { ANALYTICS_AMEND_TP_BASKET_MODAL_OPEN } from '../../constants/actionConstants';
import appConstants from '../../constants/appConstants';
import { getFormId } from '../../utils/form/configs/itemQuantity';
import OrderSummary from '../order-summary/OrderSummary';
import styles from './limited-stock-modal.scss';

export class LimitedStockModal extends PureComponent {
  static propTypes = {
    amendItemsFailed: PropTypes.bool,
    amendQuantitiesApiCallActive: PropTypes.bool,
    basketUrl: PropTypes.string,
    batchUpdateItemQuantity: PropTypes.func,
    form: PropTypes.shape({}),
    isPosCreditSubmissionError: PropTypes.bool,
    limitedStockItems: PropTypes.arrayOf(PropTypes.object),
    orderNeedsToBeRebatched: PropTypes.bool,
    pathname: PropTypes.string,
    rebatchOrderAfterBasketEdit: PropTypes.func,
    showLimitedStockModal: PropTypes.func,
    tradeplaceItemsWithUndeliverableQuantity: PropTypes.arrayOf(PropTypes.object),
  };

  static defaultProps = {
    amendItemsFailed: false,
    amendQuantitiesApiCallActive: false,
    basketUrl: '',
    batchUpdateItemQuantity: window.defaultFunc,
    form: {},
    isPosCreditSubmissionError: false,
    limitedStockItems: [],
    orderNeedsToBeRebatched: false,
    pathname: '',
    rebatchOrderAfterBasketEdit: window.defaultFunc,
    showLimitedStockModal: window.defaultFunc,
    tradeplaceItemsWithUndeliverableQuantity: [],
  };

  componentDidUpdate(prevProps) {
    const {
      limitedStockItems,
      tradeplaceItemsWithUndeliverableQuantity,
    } = this.props;

    if (this.hasModalJustOpened(prevProps, {
      limitedStockItems,
      tradeplaceItemsWithUndeliverableQuantity,
    })) {
      this.handleModalOpen();
    }

    if (this.hasModalJustClosed(prevProps, {
      limitedStockItems,
      tradeplaceItemsWithUndeliverableQuantity,
    })) {
      this.handleModalClose();
    }
  }

  handleModalOpen = () => {
    const {
      showLimitedStockModal,
      pathname,
      tradeplaceItemsWithUndeliverableQuantity,
    } = this.props;

    showLimitedStockModal(
      pathname,
      tradeplaceItemsWithUndeliverableQuantity.length ? ANALYTICS_AMEND_TP_BASKET_MODAL_OPEN : undefined,
    );
  }

  handleModalClose = () => {
    const {
      orderNeedsToBeRebatched,
      rebatchOrderAfterBasketEdit,
    } = this.props;

    if (orderNeedsToBeRebatched) {
      rebatchOrderAfterBasketEdit();
    }
  }

  hasModalJustOpened = (prevProps, collection) => {
    const prevKeyShorterThanCurrentKey = key => get(prevProps, key, []).length === 0 && collection[key].length > 0;
    const modalOpened = Object.keys(collection).some(prevKeyShorterThanCurrentKey);
    return modalOpened;
  }

  hasModalJustClosed = (prevProps, collection) => {
    const prevKeyLongerThanCurrentKey = key => get(prevProps, key, []).length && !collection[key].length;
    const modalClosed = Object.keys(collection).some(prevKeyLongerThanCurrentKey);
    return modalClosed;
  }

  getItemIds = (collection, forcedReduction) => collection.reduce((acc, item) => ({
    ...acc,
    [item.id]: forcedReduction || this.getNewQuantity(item),
  }), {});

  getNewQuantity = (item) => {
    const {
      form,
    } = this.props;

    return get(form, `${getFormId(item.id)}.values.itemQuantity`, item.stockLevel);
  }

  amendQuantities = () => {
    const {
      batchUpdateItemQuantity,
      limitedStockItems,
      tradeplaceItemsWithUndeliverableQuantity,
    } = this.props;

    if (limitedStockItems.length > 0) {
      batchUpdateItemQuantity(this.getItemIds(limitedStockItems));
    }

    if (tradeplaceItemsWithUndeliverableQuantity.length > 0) {
      batchUpdateItemQuantity(this.getItemIds(tradeplaceItemsWithUndeliverableQuantity, 1));
    }
  };

  render() {
    const {
      amendItemsFailed,
      amendQuantitiesApiCallActive,
      basketUrl,
      form,
      isPosCreditSubmissionError,
      limitedStockItems,
      tradeplaceItemsWithUndeliverableQuantity,
    } = this.props;

    const limitedStockItemsInBasket = limitedStockItems.length > 0;
    const tradeplaceItemsWithUndeliverableQuantityInBasket = tradeplaceItemsWithUndeliverableQuantity.length > 0;

    if (!limitedStockItemsInBasket && !tradeplaceItemsWithUndeliverableQuantityInBasket) {
      return null;
    }

    let bodyText;
    let headerText;
    let singleItem;

    if (limitedStockItemsInBasket) {
      singleItem = limitedStockItems.length === 1;
      const plural = singleItem ? '' : 's';
      const posCreditText = isPosCreditSubmissionError ? ' We have cancelled your payment plan with Creation Consumer Finance Ltd.' : '';

      bodyText = `Sorry, we have reduced the quantity for the following item${plural} due to limited stock.${posCreditText}`;
      headerText = 'Limited Stock';
    }

    if (tradeplaceItemsWithUndeliverableQuantityInBasket) {
      singleItem = tradeplaceItemsWithUndeliverableQuantityInBasket.length === 1;
      const plural = singleItem ? '' : 's';
      bodyText = `The item${plural} below can only be ordered one at a time.`;
      headerText = 'Stock limit';
    }

    return (
      <Modal
        header={headerText}
        hideCloseButton
        isOpen
        rootId={appConstants.ROOT}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
      >
        <div data-test="modal-limited-stock">
          <p>{bodyText}</p>

          {
            limitedStockItemsInBasket &&
            <OrderSummary
              displayHeaders={false}
              displayTotals={false}
              form={form}
              hideOutOfStockText
              hideSubtotals
              items={limitedStockItems}
              modalView
              preventEditing
              showReducedQuantityDetails
            />
          }

          {
            tradeplaceItemsWithUndeliverableQuantityInBasket &&
            <OrderSummary
              displayHeaders={false}
              displayTotals={false}
              forcedReducedQuantity={1}
              hideOutOfStockText
              hideSubtotals
              items={tradeplaceItemsWithUndeliverableQuantity}
              showReducedQuantityDetails
            />
          }

          {
            amendItemsFailed &&
            <Message
              className={styles.amendFailed}
              title="Sorry, we couldn't amend the quantity. Please return to basket."
              type="error"
            />
          }

          <div className={styles.buttonWrap}>
            {
              !amendItemsFailed &&
              <SecondaryLink
                data-action="handback"
                disabled={amendQuantitiesApiCallActive}
                external
                to={basketUrl}
              >
                Return to basket
              </SecondaryLink>
            }
            {
              amendItemsFailed &&
              <PrimaryLink
                data-action="handback"
                disabled={amendQuantitiesApiCallActive}
                external
                to={basketUrl}
              >
                Return to basket
              </PrimaryLink>
            }
            {
              !amendItemsFailed &&
              <PrimaryButton
                data-test="limited-stock-amend"
                onClick={this.amendQuantities}
                submitting={amendQuantitiesApiCallActive}
              >
                {singleItem ? 'Amend quantity and continue' : 'Amend quantities and continue'}
              </PrimaryButton>
            }
          </div>

        </div>

      </Modal>
    );
  }
}

export default connect(state => ({
  amendItemsFailed: state.orderForm.amendItemsFailed,
  amendQuantitiesApiCallActive: state.orderForm.amendQuantitiesApiCallActive,
  basketUrl: state.bff.basketUrl,
  form: state.form,
  isPosCreditSubmissionError: state.orderForm.isPosCreditSubmissionError,
  limitedStockItems: state.orderForm.limitedStockItems,
  orderNeedsToBeRebatched: state.orderForm.orderNeedsToBeRebatched,
  pathname: get(state, 'router.location.pathname', ''),
  tradeplaceItemsWithUndeliverableQuantity: state.delivery.tradeplaceItemsWithUndeliverableQuantity,
}), {
  batchUpdateItemQuantity,
  rebatchOrderAfterBasketEdit,
  showLimitedStockModal,
})(LimitedStockModal);
